import { useEffect, useCallback } from 'react';

/**
 * Hook para limpiar los overlays y clases residuales del drawer
 * Útil para evitar que se acumulen overlays y causen opacidad en el header/contenido.
 */
export const useDrawerCleanup = () => {
  const cleanupOverlays = useCallback(() => {
    document.querySelectorAll('.drawer-overlay').forEach((overlay) => overlay.remove());
    document.body.classList.remove('drawer-on');
    document.body.removeAttribute('data-kt-drawer');
    document.body.removeAttribute('data-kt-drawer-aside');
    document.body.style.removeProperty('overflow');
  }, []);

  const hideDrawer = useCallback(
    (elementId: string) => {
      const el = document.getElementById(elementId);
      if (!el) return;

      const KTDrawer = (window as any)?.KTDrawer;
      const drawerInstance = KTDrawer?.getInstance?.(el);

      if (drawerInstance?.isShown?.()) {
        drawerInstance.hide();
      }

      el.classList.remove('drawer-on');
      cleanupOverlays();
    },
    [cleanupOverlays]
  );

  return { cleanupOverlays, hideDrawer };
};

/**
 * Hook que automáticamente limpia overlays cuando cambia la ruta.
 * Usar en componentes que necesitan reaccionar a cambios de navegación.
 */
export const useDrawerCleanupOnRouteChange = (pathname: string) => {
  const { cleanupOverlays, hideDrawer } = useDrawerCleanup();

  useEffect(() => {
    hideDrawer('kt_aside');
    const timeoutId = setTimeout(cleanupOverlays, 350);
    return () => clearTimeout(timeoutId);
  }, [pathname, cleanupOverlays, hideDrawer]);

  return { cleanupOverlays, hideDrawer };
};

/**
 * Hook que configura listeners para limpiar el drawer al abrirlo/cerrarlo.
 * Incluye un MutationObserver para detectar y eliminar overlays duplicados.
 */
export const useDrawerListeners = (
  asideElementId = 'kt_aside',
  toggleButtonId = 'kt_aside_toggle'
) => {
  const { cleanupOverlays } = useDrawerCleanup();

  useEffect(() => {
    const el = document.getElementById(asideElementId);
    const toggleBtn = document.getElementById(toggleButtonId);
    if (!el) return;

    const handleDrawerHide = () => {
      setTimeout(cleanupOverlays, 100);
    };

    const handleToggleClick = () => {
      // Si el drawer no está abierto, limpiar todo
      if (!el.classList.contains('drawer-on')) {
        cleanupOverlays();
      }
    };

    const handleOverlayClick = (e: MouseEvent) => {
      if ((e.target as HTMLElement)?.classList?.contains('drawer-overlay')) {
        handleDrawerHide();
      }
    };

    // MutationObserver para detectar y eliminar overlays duplicados
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node instanceof HTMLElement && node.classList.contains('drawer-overlay')) {
            const overlays = document.querySelectorAll('.drawer-overlay');
            if (overlays.length > 1) {
              for (let i = 0; i < overlays.length - 1; i++) {
                overlays[i].remove();
              }
            }
          }
        });
      });
    });

    observer.observe(document.body, { childList: true, subtree: false });

    el.addEventListener('kt.drawer.hidden', handleDrawerHide);
    toggleBtn?.addEventListener('click', handleToggleClick, true);
    document.addEventListener('click', handleOverlayClick);

    return () => {
      observer.disconnect();
      el.removeEventListener('kt.drawer.hidden', handleDrawerHide);
      toggleBtn?.removeEventListener('click', handleToggleClick, true);
      document.removeEventListener('click', handleOverlayClick);
    };
  }, [asideElementId, toggleButtonId, cleanupOverlays]);
};
